import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetChannelConfigurations } from '@connect-x-app/hybrid-state';
import { ChannelConfiguration, Role } from '@connect-x-app/shared-domain-models';
import { IconWithTooltip } from '@connect-x-app/web-ui';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';

import { TableCell, TableHeader } from '../../../../../shared';

const columnHelper = createColumnHelper<ChannelConfiguration>();

type Props = {
  canViewRoles: boolean;
  renderCheckbox: boolean;
  roles: Role[];
};

export const useChannelConfigurationTable = ({ canViewRoles, renderCheckbox, roles }: Props) => {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useGetChannelConfigurations();
  const { t } = useTranslation();

  const channelConfigurations: ChannelConfiguration[] = useMemo(
    () => data?.pages.reduce((accu: ChannelConfiguration[], page) => [...accu, ...(page.items ?? [])], []) ?? [],
    [data?.pages],
  );

  const getRoleName = useCallback(
    (externalId: string) => {
      return roles.find(r => r.externalId === externalId)?.airlineRole ?? '';
    },
    [roles],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('title', {
        cell: props => <TableCell {...props} withCheckbox={renderCheckbox} />,
        header: props => <TableHeader {...props} title={t('CHANNEL_CONFIG.TABLE.NAME')} withCheckbox={renderCheckbox} />,
      }),
      columnHelper.accessor('description', {
        cell: props => <TableCell {...props} />,
        header: props => <TableHeader {...props} title={t('CHANNEL_CONFIG.FORM.DESCRIPTION')} />,
      }),
      columnHelper.accessor('fixedRoles', {
        cell: props => {
          if (!canViewRoles) {
            return <TableCell {...props} formatter={() => '-'} />;
          }
          const mappedIdsToNames = props.getValue().map(getRoleName);
          return <TableCell {...props} formatter={() => mappedIdsToNames} />;
        },
        header: props => (
          <div className="channel-config-table__header">
            <TableHeader {...props} title={t('CHANNEL_CONFIG.FORM.FIXED_ROLES')} />
            {!canViewRoles ? (
              <IconWithTooltip iconName="circularInfoOutline" label={t('CHANNEL_CONFIG.TABLE.PARTICIPANTS_TOOLTIP')} size={2} />
            ) : null}
          </div>
        ),
      }),
    ],
    [canViewRoles, getRoleName, renderCheckbox, t],
  );

  const table = useReactTable({
    columns,
    data: channelConfigurations,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.id,
  });

  return {
    data: channelConfigurations,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    selectedItems: table.getSelectedRowModel().rows.map(({ id }) => id),
    table,
  };
};
